import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import { BrandStoryForm } from './BrandStoryForm';
import { useAllBrandStories, useDeleteBrandStory, useUpdateBrandStory, BrandStory } from '@/hooks/useBrandStories';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

export const BrandStoriesManager = () => {
  const [editingStory, setEditingStory] = useState<BrandStory | null>(null);
  const [showForm, setShowForm] = useState(false);

  const { data: stories, isLoading } = useAllBrandStories();
  const deleteMutation = useDeleteBrandStory();
  const updateMutation = useUpdateBrandStory();

  const handleEdit = (story: BrandStory) => {
    setEditingStory(story);
    setShowForm(true);
  };

  const handleAdd = () => {
    setEditingStory(null);
    setShowForm(true);
  };

  const handleClose = () => {
    setShowForm(false);
    setEditingStory(null);
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleToggleActive = (story: BrandStory) => {
    updateMutation.mutate({
      id: story.id,
      data: { is_active: !story.is_active }
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-32 w-full mb-4" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Brand Stories</h2>
          <p className="text-muted-foreground">
            Manage brand stories with character limits to preserve visual beauty
          </p>
        </div>
        <Button onClick={handleAdd}>
          <Plus className="h-4 w-4 mr-2" />
          Add Story
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {stories?.map((story) => (
          <Card key={story.id} className="overflow-hidden">
            <div 
              className="h-32 bg-cover bg-center relative"
              style={{ 
                backgroundImage: story.background_image_url 
                  ? `url(${story.background_image_url})` 
                  : 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary-glow)))' 
              }}
            >
              <div className="absolute inset-0 bg-black/20" />
              <div className="absolute top-2 right-2">
                <Badge variant={story.is_active ? "default" : "secondary"}>
                  {story.is_active ? "Active" : "Inactive"}
                </Badge>
              </div>
            </div>
            
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start space-x-3">
                {story.image_url && (
                  <img 
                    src={story.image_url} 
                    alt={story.author}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm truncate">{story.title}</h3>
                  <p className="text-xs text-muted-foreground">
                    {story.author} • {story.role}
                  </p>
                </div>
              </div>
              
              <p className="text-sm text-muted-foreground line-clamp-2">
                {story.description}
              </p>
              
              <div className="flex items-center justify-between pt-2">
                <span className="text-xs text-muted-foreground">
                  Order: {story.sort_order}
                </span>
                
                <div className="flex space-x-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleActive(story)}
                  >
                    {story.is_active ? (
                      <Eye className="h-4 w-4" />
                    ) : (
                      <EyeOff className="h-4 w-4" />
                    )}
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(story)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Brand Story</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete "{story.title}"? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(story.id)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {showForm && (
        <BrandStoryForm
          story={editingStory}
          onClose={handleClose}
        />
      )}
    </div>
  );
};